import { useCallback, useEffect, useState } from "react";
import { useWorkService } from "../services/work.service";
import { Circulator } from "../types/external-data.types";
import { useNavigate } from "react-router-dom";

export interface CirculatorViewProps {
  circulator: Circulator;
  matterId: number;
  sheetNumber?: number;
  deficiencyId?: string;
}

export default function CirculatorView({ circulator, matterId, sheetNumber, deficiencyId }: CirculatorViewProps) {
  const navigate = useNavigate();
  const { getCirculatorWorkBySheetId } = useWorkService();
  const [circulatorWorkIds, setCirculatorWorkIds] = useState<{ [key: string]: number }>({});
  const [workIdsLoaded, setWorkIdsLoaded] = useState(false);

  useEffect(() => {
    if (sheetNumber && !workIdsLoaded) {
      getCirculatorWorkBySheetId(matterId, sheetNumber)
        .then((workIds) => {
          setCirculatorWorkIds(workIds);
          setWorkIdsLoaded(true);
        })
        .catch((error) => {
          console.error("Failed to fetch circulator work IDs:", error);
          setCirculatorWorkIds({});
          setWorkIdsLoaded(true);
        });
    } else {
      // If no sheetNumber, mark as loaded to prevent infinite loading
      if (!sheetNumber && !workIdsLoaded) {
        setWorkIdsLoaded(true);
      }
    }
  }, [matterId, sheetNumber, workIdsLoaded, getCirculatorWorkBySheetId]);

  const handleFieldClick = useCallback(
    (workId: number) => {
      if (workId > 0) {
        const fromPath = deficiencyId ? `/admin/matters/${matterId}/deficiencies/review/${deficiencyId}` : "";
        navigate(`/work/${workId}`, { state: { from: fromPath } });
      }
    },
    [deficiencyId, matterId, navigate]
  );

  if (!workIdsLoaded) {
    return <div>Loading circulator work...</div>;
  }

  // Extract work IDs with fallback to 0 if not found
  const getWorkId = (fieldNamePattern: string): number => {
    const entry = Object.entries(circulatorWorkIds).find(([key]) => {
      const matches = key.toLowerCase().includes(fieldNamePattern.toLowerCase());
      return matches;
    });
    const result = entry ? entry[1] : 0;
    return result;
  };

  // Field Button Component
  const FieldButton = ({ workId, children }: { workId: number; children: React.ReactNode }) => {
    const isDisabled = workId === 0;
    return (
      <button
        onClick={() => handleFieldClick(workId)}
        disabled={isDisabled}
        className={`col p-0 border-0 bg-transparent text-start ${isDisabled ? "text-muted" : "text-primary"}`}
        style={{
          textDecoration: isDisabled ? "none" : "underline",
          cursor: isDisabled ? "not-allowed" : "pointer",
        }}
      >
        {children}
      </button>
    );
  };

  // Field configurations
  const fieldConfigs = [
    {
      workIds: [getWorkId("ID (Front)"), getWorkId("Front")],
      label: "Registration ID (Front):",
      inputValue: circulator.registrationId ?? "",
      hasSecondButton: true,
      secondWorkIds: [getWorkId("ID (Back)"), getWorkId("Back")],
      secondLabel: "(Back)",
    },
    {
      workIds: [getWorkId("Name")],
      label: "Name:",
      inputValue: circulator.name,
      hasSecondButton: false,
    },
    {
      workIds: [getWorkId("Address")],
      label: "Address Line:",
      inputValue: circulator.address ?? "",
      hasSecondButton: false,
    },
    {
      workIds: [getWorkId("City")],
      label: "City, State, Zip:",
      inputValue: `${circulator.city}, ${circulator.state} ${circulator.postalCode}`,
      hasSecondButton: false,
    },
  ];

  return (
    <>
      {fieldConfigs.map((config, index) => (
        <div key={index} className="row">
          <div className="col">
            <FieldButton workId={config.workIds.find((id) => id > 0) || 0}>{config.label}</FieldButton>
            {config.hasSecondButton && config.secondWorkIds && config.secondLabel && (
              <FieldButton workId={config.secondWorkIds.find((id) => id > 0) || 0}>{config.secondLabel}</FieldButton>
            )}
          </div>
          <input type="text" className="col" value={config.inputValue} readOnly />
        </div>
      ))}
    </>
  );
}
